import React, { useEffect, useState } from "react";
import {TokenValidation} from "../../../../components/TokenValidation";

import { ButtonFixedLayout } from "../../../../components/ButtonFixedLayout";
import { Flex, Checkbox } from "@chakra-ui/react";
import { useButtonPosition } from "../../../../utils/buttonPositionByScreenSize";
import { Text } from "../../../../components/Text";
import { DataCard } from "../../../../components/DataCard";

export type ValidationErrorType = "blockedToken" | "exceededAttempts" | "expiredToken" | "invalidToken";

export interface TokenValidationError {
	attemptsLeft: number;
	erroType: ValidationErrorType;
}

interface IProps {
	cellphone: string;
	isLoading: boolean;
	showChooseAnotherNumberButton: boolean;
	userHasAcceptedPreviouslyToReceiveNotification: boolean;
	validationError: TokenValidationError | null;
	onLoginAnotherNumberClick: VoidFunction;
	onValidateTokenClick(token: string, hasAcceptToReceiveNotification: boolean): void;
	onResendCodeClick(value: string): void;
	resetError(): void;
}

const TokenTexts = {
	title: "Informe o código que enviamos pro número",
	link: {
		text1: "Ao validar, você concorda com os",
		text2: " termos e condições ",
		text3: "do serviço",
	},
	notice: "O código é válido por 3 minutos",
	messageSentToken: "SMS enviado",
	errors: {
		invalid: "O código informado é inválido.",
		lastStep: "A próxima tentativa é a ultima antes do bloqueio",
		expiredCode: "O código informado expirou.",
		textVariable: {
			text1: "Você tem mais",
			text2: "tentativas",
			text2Singular: "tentativa",
		},
		blocked: {
			text1: "Por segurança, bloqueamos seu acesso.",
			text2: "Você pode tentar novamente em",
			text3: "horas.",
		},
	},
	reSendCode: "Reenviar código",
	notification:
        "Aceito receber informações sobre meu empréstimo por e-mail, SMS e WhatsApp",
	buttons: {
		primary: "Tudo pronto",
		secondary: "Entrar com outro número",
	},
	timeToReSend: "Você pode pedir um novo código em",
};

export const TokenStep: React.FC<IProps> = (props) => {
	const {
		onValidateTokenClick, onResendCodeClick,
		cellphone, onLoginAnotherNumberClick,
		userHasAcceptedPreviouslyToReceiveNotification,
		validationError,
		showChooseAnotherNumberButton,
		resetError,
		isLoading,
	} = props;

	const [token, setToken] = useState<string>("");
	const [hasAcceptToReceiveNotification, setHasAcceptToReceiveNotification] = useState(true);

	const codeNotFilled = token.length < 6;
	const isDisabledSend = isLoading || codeNotFilled || !!validationError;

	const verifyAttempts = (attempts?: number): string => {
		if(!attempts) {
			return "";
		}
		if(attempts > 0) {
			return `
        ${TokenTexts.errors.textVariable.text1}
        ${validationError?.attemptsLeft}
        ${attempts === 1 ?
		TokenTexts.errors.textVariable.text2Singular :
		TokenTexts.errors.textVariable.text2}.
        `;
		}
		return TokenTexts.errors.lastStep;
	};

	const errorTexts = {
		invalidToken: {
			text1: TokenTexts.errors.invalid,
			text2: verifyAttempts(validationError?.attemptsLeft),
		},
		expiredToken: {
			text1: TokenTexts.errors.expiredCode,
			text2: verifyAttempts(validationError?.attemptsLeft),
		},
		blockedToken: {
			text1: TokenTexts.errors.blocked.text1,
			text2: `${TokenTexts.errors.blocked.text2} 3 ${TokenTexts.errors.blocked.text3}`,
		},
	};

	const formatObfuscatedNumber = (): string => {
		if (cellphone.length === 11) {
			return `${TokenTexts.title} (${cellphone.substring(0,2)}) ${cellphone.substring(2,7)}-${cellphone.substring(7)}`;
		}
		return `${TokenTexts.title} (**) *****-${cellphone.slice(-4)}`;
	};

	const verifyError = (validError: TokenValidationError | null, text: string): string => {
		if(!validError) {
			return "";
		}
		const texts = errorTexts[validError.erroType as keyof typeof errorTexts];
		return texts[text as keyof typeof texts];
	};

	const tokenError = {
		text1: verifyError(validationError, "text1"),
		text2: verifyError(validationError, "text2"),
		errorStatus: !!validationError,
	};

	const handleClickPrimaryButton = (): void => {
		onValidateTokenClick(token, hasAcceptToReceiveNotification);
	};

	const handleClickSecondaryButton = (): void => {
		onLoginAnotherNumberClick();
	};

	const handleClickReSendCode = (value: string): void => {
		onResendCodeClick(value);
	};

	const screenSizeLimit = 820;
	const buttonPosition = useButtonPosition(screenSizeLimit);

	useEffect(()=>{
		if(validationError) {
			resetError();
		}
	},[token]);

	return (
		<ButtonFixedLayout
			buttonStyles={{position: buttonPosition}}
			primaryButton={{
				title: TokenTexts.buttons.primary,
				onClick: handleClickPrimaryButton,
				variant: "solid",
				disabled: isDisabledSend,
			}}
			secondaryButton={
				showChooseAnotherNumberButton
					? {
						title: TokenTexts.buttons.secondary,
						onClick: handleClickSecondaryButton,
						variant: "outline",
						wordBreak: "break-word",
						overflowWrap: "break-word",
						whiteSpace: "normal",
						textAlign: "center",
					}
					: undefined
			}
			fixedComponent={!userHasAcceptedPreviouslyToReceiveNotification ? (
				<Checkbox
					mb={5}
					shadow=""
					defaultChecked
					onChange={(e) => setHasAcceptToReceiveNotification(e.target.checked)}
					name="notification"
					id="notif"
					colorScheme="purple"
				>
					<Text preset="preset4" fontWeight="light">{TokenTexts.notification}</Text>
				</Checkbox>
			)
				: <></>
			}
			shadowOn
			isFixed
		>
			<>
				<DataCard
					title={formatObfuscatedNumber()}
					stackContainerProps={{ spacing: "10px", width: "90%"}}
				>
					<Flex flexWrap="wrap">
						<Text preset="preset4" fontWeight="light">
							{TokenTexts.link.text1}&nbsp;
						</Text>
						<a href={"#TODO:PDF_TERMS"} download target="_blank" rel="noreferrer">
							<Text preset="preset4" fontWeight="medium" color="primary">
								{TokenTexts.link.text2}&nbsp;
							</Text>
						</a>
						<Text preset="preset4" fontWeight="light">
							{TokenTexts.link.text3}
						</Text>
					</Flex>
				</DataCard>

				<TokenValidation
					setToken={setToken}
					tokenError={tokenError}
					reSendCode={handleClickReSendCode}
					sendCode={handleClickPrimaryButton}
					cellphone={cellphone}
					isDisabledSend={isDisabledSend}
					token={token}
					messageSentToken={TokenTexts.messageSentToken}
				/>
			</>
		</ButtonFixedLayout>
	);
};
